/**
 * CompletedJobsTab — completed-jobs grid with search / filter / sort / server-side
 * pagination + Excel (.xlsx) export and a click-through to the job detail + audit
 * timeline (Quick 260616-cmpl). Endpoint VIEW_RUN_TICKETS-gated, tenant-scoped.
 */

import * as React from 'react';
import { COMPLETED_JOBS_MAX_LIMIT } from '@lodex-pro/shared/jobs-paging';
import { toast } from 'sonner';
import ExcelJS from 'exceljs';
import { apiErrorFromResponse, authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { StatusBadge } from './status-badge';
import { formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';
import { useOperators } from './use-operators';
import { useDrivers } from './use-drivers';
import { useJobTypes } from './use-job-types';
import { JobDetailModal } from './JobDetailModal';
import {
  useCompletedJobs,
  completedJobsPath,
  COMPLETED_FILTERS,
  type CompletedFilter,
  type CompletedJobsParams,
  type CompletedJobRow,
  type CompletedJobsResponse,
  type CompletedSortKey,
} from './use-completed-jobs';

const PAGE_SIZE = 50;
const EXPORT_CAP = 5000;

function localDate(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${d.getFullYear().toString()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtBbl(n: number | null): string {
  return n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtTs(iso: string | null): string {
  return iso == null ? '—' : formatTimestamp(iso);
}

const inputStyle: React.CSSProperties = {
  minHeight: '36px',
  padding: '6px 10px',
  fontSize: '13px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  borderBottom: '1px solid var(--color-border-secondary)',
  whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: '13px',
  borderBottom: '1px solid var(--color-border-secondary)',
  whiteSpace: 'nowrap',
};

interface ColumnDef {
  readonly label: string;
  readonly sortKey: CompletedSortKey | null;
  readonly render: (row: CompletedJobRow) => React.ReactNode;
  readonly cell: (row: CompletedJobRow) => string; // plain text for export
}

const COLUMNS: readonly ColumnDef[] = [
  {
    label: 'Ticket ID',
    sortKey: 'haulId',
    render: (r) => r.haulId ?? '—',
    cell: (r) => r.haulId ?? '',
  },
  {
    label: 'Operator',
    sortKey: 'operatorName',
    render: (r) => r.operatorName ?? '—',
    cell: (r) => r.operatorName ?? '',
  },
  {
    label: 'Site',
    sortKey: 'siteName',
    render: (r) => r.siteName ?? '—',
    cell: (r) => r.siteName ?? '',
  },
  {
    label: 'Driver',
    sortKey: 'driverName',
    render: (r) => r.driverName ?? '—',
    cell: (r) => r.driverName ?? '',
  },
  {
    label: 'Type',
    sortKey: 'jobTypeLabel',
    render: (r) => r.jobTypeLabel ?? '—',
    cell: (r) => r.jobTypeLabel ?? '',
  },
  {
    label: 'BBLs',
    sortKey: 'totalBbl',
    render: (r) => fmtBbl(r.totalBbl),
    cell: (r) => fmtBbl(r.totalBbl),
  },
  {
    label: 'Captured At',
    sortKey: 'capturedAt',
    render: (r) => fmtTs(r.capturedAt),
    cell: (r) => fmtTs(r.capturedAt),
  },
  {
    label: 'Completed At',
    sortKey: 'completedAt',
    render: (r) => fmtTs(r.completedAt),
    cell: (r) => fmtTs(r.completedAt),
  },
  {
    label: 'Status',
    sortKey: 'statusCode',
    render: (r) => <StatusBadge statusCode={r.statusCode} />,
    cell: (r) => r.statusLabel,
  },
];

export function CompletedJobsTab(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { data: operatorsData } = useOperators(apiOpts);
  const { data: driversData } = useDrivers(apiOpts);
  const { data: jobTypesData } = useJobTypes(apiOpts);

  const [q, setQ] = React.useState('');
  // Single-select post-work filter: All (default) / To Be Invoiced / In Review /
  // Completed. The selected filter maps to the CSV `status` param sent to the API.
  const [filterKey, setFilterKey] = React.useState<CompletedFilter['key']>('ALL');
  const status = React.useMemo<readonly string[]>(
    () => COMPLETED_FILTERS.find((f) => f.key === filterKey)?.statuses ?? [],
    [filterKey],
  );
  const [operatorId, setOperatorId] = React.useState('');
  const [driverId, setDriverId] = React.useState('');
  const [jobTypeCode, setJobTypeCode] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState(() => localDate(-2));
  const [dateTo, setDateTo] = React.useState(() => localDate(0));
  // DEC-004 / Fix #8 — the dispatcher's profile timezone; the date-range filter
  // boundaries are interpreted in this zone server-side (not UTC).
  const tz = useUserTimezone();
  const [sort, setSort] = React.useState<CompletedSortKey>('completedAt');
  const [dir, setDir] = React.useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = React.useState(0);
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [exporting, setExporting] = React.useState(false);
  const [selectedJobId, setSelectedJobId] = React.useState<string | null>(null);

  const params: CompletedJobsParams = {
    q,
    status,
    operatorId,
    driverId,
    jobTypeCode,
    dateFrom,
    dateTo,
    tz,
    sort,
    dir,
    limit: PAGE_SIZE,
    offset,
  };
  const { data, isLoading, isError } = useCompletedJobs(apiOpts, params);
  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;

  // Any filter change resets to the first page.
  const resetPage = (): void => setOffset(0);

  const onSort = (key: CompletedSortKey): void => {
    if (sort === key) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('desc');
    }
    resetPage();
  };

  const selectFilter = (key: CompletedFilter['key']): void => {
    setFilterKey(key);
    resetPage();
  };

  const toggleRow = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const pageAllSelected = jobs.length > 0 && jobs.every((j) => selected.has(j.id));
  const toggleSelectPage = (): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) jobs.forEach((j) => next.delete(j.id));
      else jobs.forEach((j) => next.add(j.id));
      return next;
    });
  };

  const operators = operatorsData?.operators ?? [];
  const drivers = driversData?.drivers ?? [];
  const jobTypes = jobTypesData?.jobTypes ?? [];

  async function fetchAllFiltered(): Promise<CompletedJobRow[]> {
    const acc: CompletedJobRow[] = [];
    let off = 0;
    // total may grow stale mid-loop; the CAP + a hard page ceiling bound it.
    // OPEN-056 — page size comes from the endpoint own cap, not a literal.
    // This loop previously hard-coded 200 in three places (the page ceiling,
    // the limit, and the offset step). That is the number whose twin on
    // GET /v1/jobs is 100, and copying it across cost us OPEN-055.
    for (let page = 0; page < EXPORT_CAP / COMPLETED_JOBS_MAX_LIMIT + 1; page++) {
      const path = completedJobsPath({ ...params, limit: COMPLETED_JOBS_MAX_LIMIT, offset: off });
      const res = await authedFetch(apiOpts, path);
      if (!res.ok) throw await apiErrorFromResponse(res, path);
      const body = (await res.json()) as CompletedJobsResponse;
      acc.push(...body.jobs);
      off += COMPLETED_JOBS_MAX_LIMIT;
      if (acc.length >= body.total || body.jobs.length === 0 || acc.length >= EXPORT_CAP) break;
    }
    return acc.slice(0, EXPORT_CAP);
  }

  const handleExport = (): void => {
    setExporting(true);
    void (async (): Promise<void> => {
      try {
        const rows =
          selected.size > 0 ? jobs.filter((j) => selected.has(j.id)) : await fetchAllFiltered();
        if (rows.length === 0) {
          toast.error('No rows to export.');
          return;
        }
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Completed Jobs');
        ws.columns = COLUMNS.map((c) => ({ header: c.label, key: c.label, width: 20 }));
        for (const r of rows) {
          const record: Record<string, string> = {};
          for (const c of COLUMNS) record[c.label] = c.cell(r);
          ws.addRow(record);
        }
        ws.getRow(1).font = { bold: true };
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `completed-jobs-${localDate(0)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        if (selected.size === 0 && rows.length >= EXPORT_CAP) {
          toast.message(`Exported the first ${EXPORT_CAP.toString()} rows (export cap).`);
        } else {
          toast.success(`Exported ${rows.length.toString()} row(s).`);
        }
      } catch {
        toast.error('Export failed. Try narrowing the filters and retry.');
      } finally {
        setExporting(false);
      }
    })();
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Filters bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          borderBottom: '1px solid var(--color-border-primary)',
          flexShrink: 0,
        }}
      >
        <input
          type="search"
          value={q}
          onChange={(e): void => {
            setQ(e.target.value);
            resetPage();
          }}
          placeholder="Search ticket, PO, site, operator, driver…"
          aria-label="Search completed jobs by ticket, reference/PO number, site, operator or driver"
          style={{ ...inputStyle, flex: 1, minWidth: '220px' }}
        />
        <input
          type="date"
          value={dateFrom}
          onChange={(e): void => {
            setDateFrom(e.target.value);
            resetPage();
          }}
          aria-label="From date"
          style={inputStyle}
        />
        <span style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>to</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e): void => {
            setDateTo(e.target.value);
            resetPage();
          }}
          aria-label="To date"
          style={inputStyle}
        />
        <div style={{ width: '180px' }}>
          <Combobox
            id="cj-operator"
            value={operatorId}
            onChange={(v): void => {
              setOperatorId(v);
              resetPage();
            }}
            options={[
              { value: '', label: 'All operators' },
              ...operators.map((o) => ({ value: o.id, label: o.name })),
            ]}
            placeholder="All operators"
            searchPlaceholder="Search operators…"
            emptyText="No operators."
          />
        </div>
        <div style={{ width: '170px' }}>
          <Combobox
            id="cj-driver"
            value={driverId}
            onChange={(v): void => {
              setDriverId(v);
              resetPage();
            }}
            options={[
              { value: '', label: 'All drivers' },
              ...drivers.map((d) => ({ value: d.driverId, label: d.driverName })),
            ]}
            placeholder="All drivers"
            searchPlaceholder="Search drivers…"
            emptyText="No drivers."
          />
        </div>
        <div style={{ width: '160px' }}>
          <Combobox
            id="cj-jobtype"
            value={jobTypeCode}
            onChange={(v): void => {
              setJobTypeCode(v);
              resetPage();
            }}
            options={[
              { value: '', label: 'All types' },
              ...jobTypes.map((t) => ({ value: t.code, label: t.label })),
            ]}
            placeholder="All types"
            searchPlaceholder="Search types…"
            emptyText="No types."
          />
        </div>
        {/* Status filter — single-select: All / To Be Invoiced / In Review / Completed. */}
        <div role="group" aria-label="Status filter" style={{ display: 'flex', gap: '4px' }}>
          {COMPLETED_FILTERS.map((f) => {
            const on = filterKey === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={(): void => selectFilter(f.key)}
                aria-pressed={on}
                style={{
                  padding: '5px 9px',
                  fontSize: '11px',
                  fontWeight: 700,
                  borderRadius: 'var(--radius-pill)',
                  cursor: 'pointer',
                  border: '1px solid var(--color-border-secondary)',
                  backgroundColor: on ? 'var(--color-accent-blue)' : 'transparent',
                  color: on ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={exporting}
          onClick={handleExport}
          style={{ marginLeft: 'auto' }}
        >
          {exporting
            ? 'Exporting…'
            : selected.size > 0
              ? `Export ${selected.size.toString()} selected`
              : 'Export all (.xlsx)'}
        </Button>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isError ? (
          <p role="alert" style={{ padding: '16px', color: 'var(--color-danger)' }}>
            Could not load completed jobs.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '32px' }}>
                  <input
                    type="checkbox"
                    checked={pageAllSelected}
                    onChange={toggleSelectPage}
                    aria-label="Select all on page"
                  />
                </th>
                {COLUMNS.map((c) => (
                  <th key={c.label} style={thStyle}>
                    {c.sortKey !== null ? (
                      <button
                        type="button"
                        onClick={(): void => onSort(c.sortKey as CompletedSortKey)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          font: 'inherit',
                          color: 'inherit',
                          textTransform: 'inherit',
                          letterSpacing: 'inherit',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        {c.label}
                        {sort === c.sortKey ? (dir === 'asc' ? '▲' : '▼') : ''}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    style={{ ...tdStyle, color: 'var(--color-text-tertiary)' }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    style={{ ...tdStyle, color: 'var(--color-text-tertiary)' }}
                  >
                    No completed jobs match the current filters.
                  </td>
                </tr>
              ) : (
                jobs.map((row) => (
                  <tr
                    key={row.id}
                    onClick={(): void => setSelectedJobId(row.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={tdStyle} onClick={(e): void => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={(): void => toggleRow(row.id)}
                        aria-label={`Select ${row.haulId ?? row.id}`}
                      />
                    </td>
                    {COLUMNS.map((c) => (
                      <td key={c.label} style={tdStyle}>
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '8px 12px',
          borderTop: '1px solid var(--color-border-primary)',
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          flexShrink: 0,
        }}
      >
        <span>
          {total === 0
            ? 'No results'
            : `${pageStart.toString()}–${pageEnd.toString()} of ${total.toString()}`}
        </span>
        <Button
          type="button"
          variant="outline"
          disabled={offset === 0}
          onClick={(): void => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Prev
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={offset + PAGE_SIZE >= total}
          onClick={(): void => setOffset((o) => o + PAGE_SIZE)}
        >
          Next
        </Button>
      </div>

      <JobDetailModal jobId={selectedJobId} onClose={(): void => setSelectedJobId(null)} />
    </div>
  );
}
